import React, { useState } from "react";
import { ShellPanel } from "@/components/shell/ShellContext";
import { PanelHeading } from "@/components/shell/SectionPanel";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useSavedViews } from "@/lib/useSavedViews";
import { cn } from "@/lib/utils";

export interface ViewItem {
  key: string;
  label: string;
  count?: number | string;
  /** Colours the count: warning for stale or due, gold for hot */
  tone?: "default" | "warning" | "gold" | "danger";
  hidden?: boolean;
}

export interface ViewGroup {
  heading?: string;
  items: ViewItem[];
}

const COUNT_TONE = { default: "text-muted-foreground", warning: "text-warning", gold: "text-gold-ink", danger: "text-danger" };

/** The module's saved views, listed in the views panel next to the icon rail */
export function ViewsPanel({
  title,
  subtitle,
  groups,
  active,
  onSelect,
  footer,
  saved,
}: {
  title: string;
  subtitle?: string;
  groups: ViewGroup[];
  active: string;
  onSelect: (key: string) => void;
  footer?: React.ReactNode;
  /** The person's own saved views: shown under the built-in ones, with "+ Save current view" */
  saved?: { module: string; getConfig: () => Record<string, any>; apply: (config: Record<string, any>) => void };
}) {
  return (
    <ShellPanel title={title} subtitle={subtitle}>
      {groups.map((g, gi) => (
        <div key={gi} className="flex flex-col gap-0.5">
          {g.heading && <PanelHeading>{g.heading}</PanelHeading>}
          {g.items
            .filter((i) => !i.hidden)
            .map((i) => {
              const on = i.key === active;
              return (
                <button
                  key={i.key}
                  type="button"
                  onClick={() => onSelect(i.key)}
                  aria-current={on ? "true" : undefined}
                  className={cn(
                    "flex h-8 items-center justify-between gap-2 rounded-md px-2.5 text-[13px] transition-colors",
                    on ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <span className="truncate">{i.label}</span>
                  {i.count !== undefined && <span className={cn("font-mono text-[11.5px]", on ? "text-primary" : COUNT_TONE[i.tone || "default"])}>{i.count}</span>}
                </button>
              );
            })}
        </div>
      ))}
      {saved && <SavedViewsSection {...saved} />}
      {footer}
    </ShellPanel>
  );
}

/** Saved views of one module: apply, remove, and save the current filters under a name */
function SavedViewsSection({ module, getConfig, apply }: { module: string; getConfig: () => Record<string, any>; apply: (config: Record<string, any>) => void }) {
  const { views, save, remove } = useSavedViews(module);
  const { success, error } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState<"" | "team" | "company">("");
  const { user } = useAuth();
  const role = String((user as any)?.UserRole?.RoleCode || "").toUpperCase();
  const canShare = role === "SUPER_ADMIN" || role === "SALES_HEAD";
  const mine = views.filter((v) => v.mine !== false);
  const theirs = views.filter((v) => v.mine === false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save(name, getConfig(), share || null);
      success("View saved", share === "company" ? `"${name.trim()}" is shared with everyone.` : share === "team" ? `"${name.trim()}" is shared with your team.` : `"${name.trim()}" is in your list.`);
      setOpen(false);
      setName("");
      setShare("");
    } catch (err: any) {
      error("Could not save the view", err?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 flex flex-col gap-0.5">
      {mine.length > 0 && <PanelHeading>Saved views</PanelHeading>}
      {mine.map((v) => (
        <div key={v.id} className="group flex items-center rounded-md hover:bg-muted">
          <button type="button" onClick={() => apply(v.config)} className="h-8 min-w-0 flex-1 truncate px-2.5 text-left text-[13px] text-muted-foreground group-hover:text-foreground">
            {v.name}
            {v.shared && <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">{v.shared === "company" ? "Company" : "Team"}</span>}
          </button>
          <button
            type="button"
            aria-label={`Remove ${v.name}`}
            onClick={() => remove(v.id).catch((err) => error("Could not remove", err?.message || "Try again"))}
            className="mr-1 hidden size-6 items-center justify-center rounded text-muted-foreground hover:text-danger group-hover:flex"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      {theirs.length > 0 && <PanelHeading>Shared with you</PanelHeading>}
      {theirs.map((v) => (
        <div key={v.id} className="group flex items-center rounded-md hover:bg-muted">
          <button type="button" onClick={() => apply(v.config)} title={v.shared_by ? `Shared by ${v.shared_by}` : undefined} className="h-8 min-w-0 flex-1 truncate px-2.5 text-left text-[13px] text-muted-foreground group-hover:text-foreground">
            {v.name}
            {v.shared_by && <span className="ml-1.5 text-[10.5px] text-muted-foreground/80">· {v.shared_by}</span>}
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setOpen(true)} className="h-[30px] px-2.5 text-left text-[13px] font-medium text-primary hover:underline">
        + Save current view
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Save current view</DialogTitle>
              <DialogDescription>Keeps the view, filters and search you have now, under a name. Only you see it unless you share it.</DialogDescription>
            </DialogHeader>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Hot leads from IndiaMART" />
            {canShare && (
              <label className="block space-y-1 text-xs font-medium text-muted-foreground">
                Who can use it
                <select value={share} onChange={(e) => setShare(e.target.value as "" | "team" | "company")} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm font-normal text-foreground">
                  <option value="">Only me</option>
                  <option value="team">My team</option>
                  {role === "SUPER_ADMIN" && <option value="company">Everyone</option>}
                </select>
              </label>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !name.trim()}>
                Save view
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
